import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { AuthApiService } from 'src/app/apis/auth-api.service';
import { Common } from 'src/app/lib/common-methods';
import { ReservationService } from 'src/app/lib/reservation.service';
import { UtilsService } from 'src/app/lib/utils.service';
import { CustomerType, PmsInfo, PmsInfoEntry } from 'src/app/model/customer';
import { ChainAccount } from 'src/app/model/employee';
import { Menu } from 'src/app/model/login-response.dto';
import { Reservation, ReservationStatus } from 'src/app/model/reservation';
import { ReservationTag, ReservationTagCategory } from 'src/app/model/reservation-tag';
import { Source } from 'src/app/model/source';
import { environment } from 'src/environments/environment';
import * as ReservationListActions from '../../store/actions/reservation-list.actions';
import * as ReservationActions from '../../store/actions/reservation.actions';
import * as AppSelectors from '../../store/selectors/app.selectors';
import * as EmployeeSelectors from '../../store/selectors/employee.selectors';
import * as ReservationListSelectors from '../../store/selectors/reservation-list.selectors';

declare let $: any;

@Component({
  selector: '[app-reservation-list-item]',
  templateUrl: './reservation-list-item.component.html',
  styleUrls: ['./reservation-list-item.component.scss']
})
export class ReservationListItemComponent implements OnInit, AfterViewInit, OnDestroy {

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private router: Router, private store: Store<{}>,
    private translateService: TranslateService,
    private reservationService: ReservationService,
    private authService: AuthApiService,
    private utilsService: UtilsService) { }

  @ViewChild('reservationdetails') reservationDetails: ElementRef | null = null;

  restaurantIds$: Observable<string[] | undefined> = this.store.pipe(select(EmployeeSelectors.selectRestaurantIds));
  restaurants$: Observable<ChainAccount[] | undefined> = this.store.pipe(select(EmployeeSelectors.selectRestaurants));
  pendingReservations$: Observable<Reservation[]> = this.store.pipe(select(ReservationListSelectors.selectPendingReservations));
  expandedReservationIds$: Observable<string[]> = this.store.pipe(select(ReservationListSelectors.expandedReservationIds));
  restaurantHasExtraInfo$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectRestaurantHasExtraInfo));
  restaurantHasOnlineCategories$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectRestaurantHasOnlineCategories));
  restaurantId$: Observable<number> = this.store.pipe(select(AppSelectors.selectRestaurantId));
  menus$: Observable<Menu[]> = this.store.pipe(select(AppSelectors.selectMenus));
  menus: Menu[] = [];
  customerTypes$: Observable<CustomerType[]> = this.store.pipe(select(AppSelectors.selectCustomerTypes));
  customerTypes: CustomerType[] = [];
  tags$: Observable<ReservationTagCategory[] | null> = this.store.pipe(select(AppSelectors.selectTags));
  restaurantHasExtraInfo: boolean = false;
  pendingReservations: Reservation[] = [];
  isExpanded: boolean = false;
  readOnlyMode: boolean = false;
  showReservationDetails: boolean = true;

  @Input() reservation: Reservation | undefined;
  @Input() index: number = 0;
  @Input() showGlobalSearchInfo: boolean = false;
  @Input() isOffline: boolean = false;

  restaurantIdsToNames: { [id: string]: string; } = {};

  allergies: string[] = [];
  reservationAllergies: string[] = [];
  customerAllergies: string[] = [];
  allTags: ReservationTagCategory[] = [];
  reservationTags: ReservationTag[] = [];
  uniqueReservationTagsByCategory: ReservationTag[] = [];
  customerTags: ReservationTag[] = [];
  uniqueCustomerTagsByCategory: ReservationTag[] = [];
  reservationAndCustomerTags: ReservationTag[] = [];
  uniqueReservationAndCustomerTagsByCategory: ReservationTag[] = [];
  dietaryTagCategories: ReservationTagCategory[] = [];
  dietaryTags: ReservationTag[] = [];

  showTags: boolean = environment.showTags;

  canReadTags = this.utilsService.canRead('Tags');
  canReadAllergies = this.utilsService.canRead('Allergies');

  async ngOnInit(): Promise<void> {
    const restaurants = await firstValueFrom(this.restaurants$);

    this.readOnlyMode = !this.utilsService.canWrite('EditReservation');

    this.refreshReservationTagsPerType();

    if (restaurants != null) {
      for (const restaurant of restaurants) {

        if (restaurant.restaurantId == null || restaurant.restaurantTitle == null) {
          continue;
        }

        this.restaurantIdsToNames[restaurant.restaurantId] = restaurant.restaurantTitle;
      }
    }

    this.pendingReservations$.pipe(takeUntil(this.destroy$)).subscribe((pendingReservations) => {
      this.pendingReservations = pendingReservations;
    });

    this.menus$.pipe(takeUntil(this.destroy$)).subscribe((menus) => {
      this.menus = menus;
    });

    this.restaurantHasExtraInfo$.pipe(takeUntil(this.destroy$)).subscribe((restaurantHasExtraInfo) => {
      this.restaurantHasExtraInfo = restaurantHasExtraInfo;
    });

    this.expandedReservationIds$.pipe(takeUntil(this.destroy$)).subscribe((expandedReservationIds) => {
      const reservationId = this.reservation?.reservationDetails.reservationId as string;
      this.isExpanded = expandedReservationIds.includes(reservationId);
    });

    const reservationAllergies = (this.reservation?.reservationDetails?.allergy ?? '').split(',').filter(a => !Common.isNullOrEmpty(a));
    const customerAllergies = (this.reservation?.customerDetails?.allergy ?? '').split(',').filter(a => !Common.isNullOrEmpty(a));

    this.reservationAllergies = reservationAllergies;
    this.customerAllergies = customerAllergies;
    this.allergies = reservationAllergies.concat(customerAllergies);

    this.customerTypes$.pipe(takeUntil(this.destroy$)).subscribe((customerTypes) => {
      this.customerTypes = customerTypes;
    });

    this.tags$.pipe(takeUntil(this.destroy$)).subscribe((tags) => {
      this.allTags = tags ?? [];

      this.dietaryTagCategories = this.allTags.filter(tagCategory => tagCategory.isDietary());
      const dietaryTagCategoryIds = this.dietaryTagCategories.map(category => category.id);
      this.dietaryTags = this.reservationTags.filter(tag => dietaryTagCategoryIds.some(id => id === tag.categoryId));

      this.refreshReservationTagsPerType();
    });

  }

  ngAfterViewInit() {
    this.refreshShowReservationDetails();
  }

  refreshShowReservationDetails() {
    setTimeout(() => {
      this.showReservationDetails = this.reservationDetails?.nativeElement?.textContent?.trim().length > 0;
    }, 100);
  }

  statusToIcon(status: ReservationStatus) {
    return Common.getReservationStatusIcon(status);
  }

  statusToClass(status: ReservationStatus) {
    return Common.getReservationClass(status);
  }

  async navigateToReservation(reservation: Reservation) {

    if (this.isOffline) {
      return;
    }

    const navigate = () => {
      const date = moment(reservation.reservationDetails.arrivalTime);

      const arrivalTime = moment(reservation.reservationDetails.arrivalTime).toDate();
      const isCurrentTimeNextDayMorning = Common.timeIsNextDayMorning(arrivalTime);

      if (isCurrentTimeNextDayMorning) {
        date.add(-1, 'days');
      }

      // set date
      this.store.dispatch({ type: ReservationListActions.setDate.type, date: date.format('yyyy-MM-DD') });

      this.router.navigate([`/reservations/edit/${reservation.reservationDetails.reservationId ?? reservation.reservationDetails.tmpId}`]);
    }

    const activeRestaurantId = await firstValueFrom(this.restaurantId$);

    if (activeRestaurantId.toString().trim() === reservation.reservationDetails?.restaurantId?.trim()) {
      navigate();
      return;
    }

    // prevent flickering
    $('body').css('opacity', '0');

    // the reservation belongs to another restaurant (global search)
    if (reservation.reservationDetails.restaurantId == null) {
      console.log("weird, restaurant id is null");
      return;
    }

    await this.authService.changeRestaurant(reservation.reservationDetails.restaurantId);

    navigate();
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }

  isPending(): boolean {
    if (this.reservation == null) {
      return false;
    }
    return this.pendingReservations
      .some(reservation => reservation.reservationDetails.reservationId === this.reservation?.reservationDetails.reservationId);
  }

  onShowChangeStatusModal(event: MouseEvent) {
    event.stopPropagation();

    if (this.readOnlyMode) {
      return;
    }

    if (this.showGlobalSearchInfo) {
      return;
    }

    if (!this.canQuickChangeStatus()) {
      return;
    }

    this.store.dispatch({ type: ReservationActions.setQuickChangeStatusReservation.type, reservation: this.reservation });
  }

  canQuickChangeStatus() {
    return !this.readOnlyMode && this.utilsService.canWrite('QuickStatusChange');
  }

  getCustomerTypeColor(value: string | undefined, fallbackColor: string = '#D3CECD') {
    const customerType = this.customerTypes.find(customerType => customerType.value === value);

    if (customerType == null || !customerType.color) {
      return fallbackColor;
    }

    return customerType.color;
  }

  getCustomerTypeTextColor(value: string | undefined) {
    const customerType = this.customerTypes.find(customerType => customerType.value === value);

    if (customerType == null || customerType.value === 'PPV') {
      return 'white';
    }

    return 'black';
  }

  wrapCustomerType(customerType: string | undefined) {
    if (customerType === 'R&VIP') {
      return 'R& VIP';
    }

    return customerType;
  }

  hasSource(reservation: Reservation): boolean {
    return reservation.sourceDetails != null && !Common.isNullOrEmpty(this.getSourceDesctiption(reservation.sourceDetails));
  }

  getSourceDesctiption(source: Source): string {
    var items = [
      source.companyTitle,
      (source.contactName ?? "") + " " + (source.contactSurname ?? ""),
      source.contactTelephone,
      source.contactMail
    ];

    return items
      .filter(s => !Common.isNullOrEmpty(s))
      .join(", ");
  }

  getTagColor(tag: ReservationTag) {
    return this.allTags?.find(tagCategory => tagCategory.id === tag.categoryId)?.color;
  }

  getSourceCaption() {
    if (this.reservation == null || this.reservation.sourceDetails == null) {
      return '';
    }

    if (!this.utilsService.canRead('Source')) {
      return '';
    }

    if (this.reservation.sourceDetails != null && this.reservation.sourceDetails.sourceCategory == "EB2CPLATFORM") {
      return '';
    }

    const sourceCaption = this.reservationService.getSourceCaption(this.reservation.sourceDetails, true);

    if (sourceCaption == null || sourceCaption.trim().length <= 0) {
      return '';
    }

    return `(${sourceCaption})`;
  }

  showExtraInfo(extraInfo: string | null | undefined) {
    if (extraInfo == null) {
      return false;
    }

    return extraInfo.trim() != '-';
  }

  refreshReservationTagsPerType() {
    this.reservationTags = (JSON.parse(JSON.stringify(this.reservation?.reservationDetails.reservationTags ?? [])))
      .sort((a: ReservationTag, b: ReservationTag) => {
        return this.reservationService.tagByCategoryOrderComperator(a, b);
      });


    this.uniqueReservationTagsByCategory = [...new Map(this.reservationTags
      .filter(tag => !this.dietaryTagCategories?.some(tagCategory => tagCategory.id === tag.categoryId))
      .map(item =>
        [item.categoryId, item])).values()];

    this.customerTags = (JSON.parse(JSON.stringify(this.reservation?.customerDetails.customerTags ?? [])))
      .sort((a: ReservationTag, b: ReservationTag) => {
        return this.reservationService.tagByCategoryOrderComperator(a, b);
      });

    this.uniqueCustomerTagsByCategory = [...new Map(this.customerTags
      // .filter(tag => !this.dietaryTagCategories?.some(tagCategory => tagCategory.id === tag.categoryId))
      .map(item =>
        [item.categoryId, item])).values()];

    // if customer allergy is filled display a tag above name
    if (this.dietaryTagCategories.length > 0
      && !Common.isNullOrEmpty(this.reservation?.customerDetails.allergy)
      && !this.uniqueCustomerTagsByCategory.some(t => this.dietaryTagCategories.some(dtg => dtg.id === t.categoryId))) {
      this.uniqueCustomerTagsByCategory.unshift(this.dietaryTagCategories[0].tags[0]);
    }

    this.reservationAndCustomerTags = this.customerTags.concat(this.reservationTags);
    this.uniqueReservationAndCustomerTagsByCategory = this.uniqueReservationTagsByCategory.concat(this.uniqueCustomerTagsByCategory);

    this.refreshShowReservationDetails();
  }

  showReservationTagsOnDetails() {
    return (this.canReadAllergies && this.reservationAllergies.length > 0)
      || (this.canReadTags && this.reservationTags.length > 0);
  }

  showCustomerTagsOnDetails() {
    return (this.canReadAllergies && this.customerAllergies.length > 0)
      || (this.canReadTags && this.customerTags.length > 0);
  }

  hasDiscount() {
    return this.reservationService.hasDiscount(this.reservation);
  }
  
  showOrderDetails() {
    var id = this.reservation?.reservationDetails?.reservationId;

    if (id == null || id.trim() == '') return;

    this.store.dispatch({ type: ReservationListActions.setOrderDetailsReservation.type, reservationId: id });    
  }

  parseMetadata(metadata: string | null | undefined): PmsInfo | null {
  if (!metadata?.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(metadata) as PmsInfo;
    return parsed && Array.isArray(parsed.values) ? parsed : null;
  } catch {
    return null;
  }
}

getMetadataEntries(metadata: string | PmsInfo | null | undefined): PmsInfoEntry[] {
  if (!metadata) {
    return [];
  }

  let parsed: PmsInfo | null = null;

  if (typeof metadata === 'string') {
    parsed = this.parseMetadata(metadata);
  } else {
    parsed = metadata;
  }

  return (parsed?.values ?? []).filter(entry => !!entry?.value?.trim());
}

getMetadataCaption(entry: PmsInfoEntry): string {
  const currentLang = this.translateService.currentLang || 'en';

  return currentLang === 'el'
    ? (entry.captionGreek || entry.captionEnglish || entry.key || '')
    : (entry.captionEnglish || entry.captionGreek || entry.key || '');
}

splitMetadataValue(value?: string): string[] {
  if (!value?.trim()) {
    return [];
  }

  return value.split('|').map(x => x.trim()).filter(Boolean);
}

hasMetadata(metadata: string | null | undefined): boolean {
  return this.getMetadataEntries(metadata).length > 0;
}

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
